import React, { useState } from "react";
import { useApiQuery } from '../../hooks/useApiQuery';
import AdminLayout from "./AdminDashboardLayout";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Shield, Users, Settings, LogIn, LogOut, Plus, Clock, CalendarRange, ChevronDown } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subYears } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { request } from '../../utils/api';

interface DashboardMetric {
  value: number;
  change: number;
  trendType: 'PERCENTAGE' | 'COUNT' | 'ATTENDANCE';
}

interface DashboardData {
  totalRegistrations: DashboardMetric;
  activePartners: DashboardMetric;
  activeCanvassers: DashboardMetric;
  completionRate: DashboardMetric;
}

interface DashboardResponse {
  success: boolean;
  message: string;
  data: DashboardData;
}

// Audit Log interfaces
interface AuditLog {
  id: number;
  actor: {
    id: number;
    email: string;
    fullName: string;
    role: string;
    partner?: {
      id: number;
      name: string;
      partnerIdentifier: string;
    };
  } | null;
  partner: {
    id: number;
    name: string;
    partnerIdentifier: string;
  } | null;
  eventType: string;
  action: string;
  metadata: Record<string, any>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditLogResponse {
  success: boolean;
  message: string;
  data: {
    content: AuditLog[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

// Activity Log interfaces
interface ActivityActor {
  id: number;
  email: string;
  fullName: string;
  role: string;
  partner?: {
    id: number;
    name: string;
    partnerIdentifier: string;
    orgType: string;
    legalName: string;
    contact: string;
    website: string;
  } | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginIp?: string;
  lastLoginLocation?: string;
  lastLoginAt?: string;
  phone?: string;
  mode: string;
}

interface ActivityPartner {
  id: number;
  name: string;
  orgType: string;
  partnerIdentifier: string;
  legalName: string;
  contact: string;
  website: string;
  createdAt: string;
  updatedAt: string;
  enabled: boolean;
  status: string;
  enabledModules: string[];
}

interface ActivityLog {
  id: number;
  partner: ActivityPartner | null;
  actor: ActivityActor;
  type: string;
  message: string;
  createdAt: string;
}

interface ActivityResponse {
  success: boolean;
  message: string;
  data: {
    content: ActivityLog[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

type DashboardPeriod = 'weekly' | 'month' | 'year' | 'all';
type FilterMode = 'period' | 'custom';

const periodOptions: Array<{
  value: DashboardPeriod;
  label: string;
  description: string;
}> = [
  { value: 'weekly', label: 'This week', description: 'Short-term movement' },
  { value: 'month', label: 'This month', description: 'Balanced tracking' },
  { value: 'year', label: 'This Year', description: 'Long-range performance' },
  { value: 'all', label: 'All time', description: 'No date limits' },
];

export default function AdminDashboard() {

	const navigate = useNavigate()

  const today = new Date();
  const [startDate, setStartDate] = useState<Date | null>(subYears(today, 2));
  const [endDate, setEndDate] = useState<Date | null>(today);
  const [period, setPeriod] = useState<DashboardPeriod>('year');
  const [filterMode, setFilterMode] = useState<FilterMode>('period');
	
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'admin-dashboard-cards',
      filterMode,
      filterMode === 'custom' && startDate ? format(startDate, 'yyyy-MM-dd') : null,
      filterMode === 'custom' && endDate ? format(endDate, 'yyyy-MM-dd') : null,
      filterMode === 'period' ? period : null,
    ],
    queryFn: () =>
      request<DashboardResponse>({
        url: '/admin/dashboard/cards',
        method: 'GET',
        params:
          filterMode === 'custom'
            ? {
                startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
                endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
              }
            : period === 'all'
              ? undefined
              : {
                  period,
                },
      }),
    enabled: filterMode === 'period' || Boolean(startDate && endDate),
    placeholderData: keepPreviousData,
  });

  // API call for audit logs (limited to 5 items)
  const { data: auditData } = useApiQuery<AuditLogResponse>({
    queryKey: ['audit-logs-dashboard'],
    request: {
      url: '/audit-logs',
      method: 'GET',
      params: {
        page: 0,
        size: 5
      }
    }
  });

  // API call for activity logs (limited to 6 items)
  const { data: activityData } = useApiQuery<ActivityResponse>({
    queryKey: ['activity-logs-dashboard'],
    request: {
      url: '/activity-logs',
      method: 'GET',
      params: {
        page: 0,
        size: 5
      }
    }
  });

  const dashboardData = data?.data;

  // Helper functions for icons and colors
  const getAuditEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN':
        return <LogIn className="w-3 h-3 text-green-600" />;
      case 'LOGOUT':
        return <LogOut className="w-3 h-3 text-orange-600" />;
      case 'PARTNER_CREATED':
        return <Users className="w-3 h-3 text-blue-600" />;
      case 'PARTNER_UPDATED':
        return <Settings className="w-3 h-3 text-purple-600" />;
      default:
        return <Shield className="w-3 h-3 text-gray-600" />;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'PARTNER_ONBOARDED':
        return <Plus className="w-3 h-3 text-green-600" />;
      case 'PARTNER_UPDATED':
        return <Settings className="w-3 h-3 text-purple-600" />;
      case 'SYSTEM_EVENT':
        return <Shield className="w-3 h-3 text-blue-600" />;
      case 'USER_LOGIN':
        return <LogIn className="w-3 h-3 text-green-600" />;
      case 'USER_LOGOUT':
        return <LogOut className="w-3 h-3 text-orange-600" />;
      default:
        return <Clock className="w-3 h-3 text-gray-600" />;
    }
  };

  const formatTrendText = (metric: DashboardMetric) => {
    const { change, trendType } = metric;
    const sign = change >= 0 ? '+' : '';
    
    switch (trendType) {
      case 'PERCENTAGE':
        return `${sign}${change}%`;
      case 'COUNT':
        return `${sign}${change}`;
      case 'ATTENDANCE':
        return `${change} online`;
      default:
        return `${sign}${change}`;
    }
  };

  const stats = dashboardData ? [
    { 
      label: 'Total Registrations', 
      value: dashboardData.totalRegistrations.value.toLocaleString(), 
      sub: formatTrendText(dashboardData.totalRegistrations),
      accent: 'bg-sky-500',
    },
    { 
      label: 'Active Partners', 
      value: dashboardData.activePartners.value.toString(), 
      sub: formatTrendText(dashboardData.activePartners),
      accent: 'bg-violet-500',
    },
    { 
      label: 'Active Canvassers', 
      value: dashboardData.activeCanvassers.value.toString(), 
      sub: formatTrendText(dashboardData.activeCanvassers),
      accent: 'bg-amber-500',
    },
    { 
      label: 'Completion Rate', 
      value: `${dashboardData.completionRate.value}%`, 
      sub: formatTrendText(dashboardData.completionRate),
      accent: 'bg-emerald-500',
    },
  ] : [];

  const rangeLabel = startDate && endDate
    ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    : 'Select a date range';

	const quickActions = [
		{ label: 'Add Legal Pages', icon: <InsightsOutlinedIcon fontSize="small" />, url:"/admin/manage#Template" },
		{ label: 'Add New Election Info', icon: <Add fontSize="small" />, url:"/admin/manage#election" },
		{ label: 'Update Existing Election', icon: <Add fontSize="small" />, url:"/admin/manage#update" },
		{ label: 'Manage Partners', icon: <PeopleAltOutlinedIcon fontSize="small" />, url:"/admin/partners" },
		{ label: 'Manage Export Requests', icon: <UploadFileOutlinedIcon fontSize="small" />, url:"/admin/exports" },
		{ label: 'System Settings', icon: <SettingsOutlinedIcon fontSize="small" />, url:"/admin/settings" },
	];
	// Show loading state
	if (isLoading && !dashboardData) {
		return (
			<AdminLayout role="SYSTEM ADMIN">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
				</div>
			</AdminLayout>
		);
	}

	// Show error state
	if (error && !dashboardData) {
		return (
			<AdminLayout role="SYSTEM ADMIN">
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<div className="flex items-center">
						<span className="text-red-800">Failed to load dashboard data. Please try again.</span>
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout role="SYSTEM ADMIN">
			<div className="space-y-6 text-gray-900">
				<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">System Overview</h2>
              <p className="mt-1 text-sm text-slate-500">
                {filterMode === 'period' ? `Showing ${period} card trends.` : `Custom range: ${rangeLabel}`}
              </p>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => setFilterMode('period')}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                    filterMode === 'period' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Period
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode('custom')}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                    filterMode === 'custom' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Custom range
                </button>
              </div>

              {filterMode === 'period' ? (
                <div className="inline-flex flex-wrap rounded-xl border border-slate-200 bg-white p-1">
                  {periodOptions.map((option) => {
                    const isActive = period === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setPeriod(option.value)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition ${
                          isActive
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="relative min-w-[250px]">
                  <CalendarRange className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <DatePicker
                    selected={startDate}
                    onChange={(dates: [Date | null, Date | null]) => {
                      const [nextStart, nextEnd] = dates;
                      setStartDate(nextStart);
                      setEndDate(nextEnd);
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    maxDate={today}
                    dateFormat="MMM dd, yyyy"
                    monthsShown={2}
                    placeholderText="Select date range"
                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

				{/* Stats cards */}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{stats.map((stat) => (
						<div
              key={stat.label}
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className=" flex items-center justify-between gap-3">
                {isFetching && (
                  <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                    Updating
                  </span>
                )}
              </div>
              <div className="relative">
                <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">{stat.value}</p>
                {stat.sub && (
                  <div className="mt-3 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {stat.sub}
                  </div>
                )}
              </div>
						</div>
					))}
				</div>

				{/* Lower sections */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
					{/* Quick Actions */}
					<div className="bg-white rounded-md shadow p-4">
						<h3 className="font-semibold mb-3">Quick Actions</h3>
						<div className="space-y-2">
							{quickActions.map((q) => (
								<button key={q.label} onClick={() => navigate(q.url)} className="w-full flex items-center gap-2 bg-gray-100 hover:bg-gray-200 rounded-md px-3 py-2 text-left">
									{q.icon}
									<span>{q.label}</span>
								</button>
							))}
						</div>
					</div>

					{/* Recent Activity */}
					<div className="bg-white rounded-md shadow p-4">
						<h3 className="font-semibold mb-3">Recent Activity</h3>
						<ul className="divide-y">
							{activityData?.data?.content?.map((activity) => (
								<li key={activity.id} className="py-2 flex justify-between">
									<div className="flex items-start space-x-2">
										{getActivityIcon(activity.type)}
										<div>
											<p className="text-sm font-medium">{activity.message}</p>
											<p className="text-xs text-gray-400">
												{activity.actor?.fullName} • {activity.actor?.role.replace('_', ' ')}
											</p>
										</div>
									</div>
									<span className="text-xs text-gray-400 whitespace-nowrap">
										{formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
									</span>
								</li>
							))}
							{!activityData?.data?.content?.length && (
								<li className="py-4 text-center text-gray-500 text-sm">
									No recent activity
								</li>
							)}
						</ul>
					</div>

					{/* System Audit Log */}
					<div className="bg-white rounded-md shadow p-4">
						<h3 className="font-semibold mb-3">System Audit Log</h3>
						<ul className="divide-y">
							{auditData?.data?.content?.map((log) => (
								<li key={log.id} className="py-2 flex justify-between">
									<div className="flex items-start space-x-2">
										{getAuditEventIcon(log.eventType)}
										<div>
											<p className="text-sm font-medium">{log.action}</p>
											<p className="text-xs text-gray-400">
												{log.actor?.fullName || 'System'} • {log.eventType.replace('_', ' ')}
											</p>
										</div>
									</div>
									<span className="text-xs text-gray-400 whitespace-nowrap">
										{formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
									</span>
								</li>
							))}
							{!auditData?.data?.content?.length && (
								<li className="py-4 text-center text-gray-500 text-sm">
									No audit logs available
								</li>
							)}
						</ul>
					</div>
				</div>
			</div>
		</AdminLayout>
	);
}
